import { useEffect, useState } from "react";
import axios from "axios";
import { Upload, Trash2, LogOut, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bl_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [category, setCategory] = useState("gallery");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e) {
      setError("كلمة السر خاطئة");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/images`, { params: { category } });
      setImages(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line
  }, [token, category]);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/admin/upload?category=${category}`, fd, {
          headers: { ...authHeader, "Content-Type": "multipart/form-data" },
        });
      }
      await load();
    } catch (e) {
      alert("رفع فشل — تأكد من الاتصال أو سجّل دخول من جديد.");
      if (e?.response?.status === 401) logout();
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف الصورة؟")) return;
    await axios.delete(`${API}/admin/images/${id}`, { headers: authHeader });
    await load();
  };

  const publicUrl = (p) => `${API}/files/${p}`;

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#171717] p-6">
        <form
          onSubmit={login}
          className="w-full max-w-sm space-y-6 border border-white/10 bg-[#1c1c1c] p-8 rounded-sm"
          dir="rtl"
        >
          <div>
            <div className="overline">Admin · لوحة التحكم</div>
            <h1 className="font-arabic text-2xl font-bold mt-2">تسجيل الدخول</h1>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="كلمة السر"
            className="w-full bg-black border border-white/10 rounded-sm px-4 py-3 text-white outline-none focus:border-[#FD1843]"
            autoFocus
          />
          {error && <div className="text-red-400 text-sm">{error}</div>}
          <button type="submit" className="btn-cyan w-full justify-center font-arabic font-bold">
            دخول
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#171717] text-white p-6 md:p-10" dir="rtl">
      <div className="max-w-[1400px] mx-auto space-y-8">
        <header className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline" dir="ltr">Admin Panel</div>
            <h1 className="font-arabic text-3xl md:text-4xl font-bold mt-2">
              إدارة صور <span className="text-[#FD1843]">الموقع</span>
            </h1>
          </div>
          <button onClick={logout} className="btn-ghost font-arabic">
            <LogOut size={16} /> خروج
          </button>
        </header>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2" dir="ltr">
          {["gallery", "portfolio", "clients"].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full border text-sm ${
                category === c
                  ? "bg-[#FD1843] border-[#FD1843] text-[#171717]"
                  : "border-white/15 text-white/70 hover:border-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Upload */}
        <label className="block border-2 border-dashed border-white/15 rounded-sm p-10 text-center cursor-pointer hover:border-[#FD1843] transition">
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files && upload(Array.from(e.target.files))}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-3 text-white/70">
              <Loader2 className="animate-spin" size={18} /> يجري الرفع…
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload className="text-[#FD1843]" size={28} />
              <div className="font-arabic text-lg font-bold">اضغط لرفع صور</div>
              <div className="text-white/50 text-sm">JPG / PNG / WEBP — تكدر ترفع عدة صور بنفس الوقت</div>
            </div>
          )}
        </label>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full text-white/50 text-center py-10">…</div>
          ) : images.length === 0 ? (
            <div className="col-span-full text-white/50 text-center py-10 font-arabic">
              ماكو صور بعد — ارفع أول صورة من فوق
            </div>
          ) : (
            images.map((img) => (
              <div key={img.id} className="relative group bg-[#1c1c1c] border border-white/10 rounded-sm overflow-hidden">
                <img src={publicUrl(img.storage_path)} alt="" className="w-full h-56 object-cover" />
                <button
                  onClick={() => remove(img.id)}
                  className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 w-9 h-9 rounded-full bg-red-500 grid place-items-center transition"
                  title="حذف"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  onClick={() => navigator.clipboard.writeText(publicUrl(img.storage_path))}
                  className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 text-[10px] font-mono bg-black/70 border border-white/20 px-2 py-1 rounded transition"
                  title="انسخ رابط الصورة"
                >
                  نسخ الرابط
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
